import { useCallback } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import NotesTable from "@/features/global/components/NotesTable";
import TasksTable from "@/features/global/components/TasksTable";
import { getNoteParentLink } from "@/features/global/components/NotesTable/noteHelpers";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import { type CRMNote, NoteNotableValues } from "@/types";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import { getEventTypeName } from "../../utils";
import { EventOutletContext } from "../_layout";
import { EVENT_NOTES_QUERY, type EventNotesQueryResponse } from "./queries";

export default function EventNotesPage() {
  const { eventSlug = "" } = useParams();
  const basePath = useEventsBasePath();
  const { eventData } = useOutletContext<EventOutletContext>();

  const isSectionScoped = useIsSectionScopedForEvents();
  const canAccessActivityManagement =
    useActivityTypePermissions(eventData?.activityType ?? { title: getEventTypeName(eventData) }) &&
    (!isSectionScoped || !!eventData?.section?.permissions?.manageEvents);
  const canManageTasks = useCanManageTasksBool();
  const showTasks = canManageTasks;

  const { highlightNoteId, openNoteId, getCopyLink, setOpenNoteInUrl, tableRef } = useNoteDeepLink({
    dataReady: true
  });

  const getParentLink = useCallback(
    (note: CRMNote) =>
      getNoteParentLink(note, {
        pgaId: "",
        eventSlug,
        eventBasePath: basePath
      }),
    [eventSlug, basePath]
  );

  return (
    <div ref={tableRef} className="pb-6">
      <NotesTable
        variant="top-level"
        notesQuery={EVENT_NOTES_QUERY}
        queryVariables={{ slug: eventSlug }}
        selectConnection={(data: EventNotesQueryResponse | undefined) => data?.eventNotes}
        notableTypeFilter={[NoteNotableValues.EVENT]}
        tableHeading="Event Notes"
        tableBody="View all notes created for this event"
        getCopyLink={getCopyLink}
        getParentLink={getParentLink}
        highlightNoteId={highlightNoteId}
        openNoteId={openNoteId}
        onOpenNoteChange={setOpenNoteInUrl}
        noteEditing={{
          enabled: canAccessActivityManagement,
          notableId: eventData?.id || "",
          notableType: NoteNotableValues.EVENT,
          refetchQueries: [EVENT_NOTES_QUERY],
          eventSlug,
          objectType: "event"
        }}
      />
      {showTasks && (
        <TasksTable
          referenceableId={eventData?.id || ""}
          referenceableType="EVENT"
          tableHeading="Event Tasks"
          tableBody="View and manage tasks for this event"
          hideControls
        />
      )}
    </div>
  );
}
